package com.example.borshi.web.orders

import androidx.compose.runtime.*
import com.example.borshi.web.data.OrderItemType
import com.example.borshi.web.data.WebOrder
import com.example.borshi.web.data.WebOrderItem
import com.example.borshi.web.firebase.FirebaseService
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

@Composable
fun OrderHistoryScreen(@Suppress("UNUSED_PARAMETER") onBack: () -> Unit) {
    var orders by remember { mutableStateOf<List<WebOrder>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun loadOrders() {
        scope.launch {
            isLoading = true
            errorMessage = null
            try {
                orders = FirebaseService.getOrders()
            } catch (e: Throwable) {
                console.error("Failed to load orders:", e)
                errorMessage = e.message ?: "Failed to load order history"
            } finally {
                isLoading = false
            }
        }
    }

    LaunchedEffect(Unit) { loadOrders() }

    Div({
        style {
            maxWidth(900.px)
            property("margin", "0 auto")
            padding(24.px, 16.px)
            width(100.percent)
            backgroundColor(Color("#F5F5F5"))
            minHeight(100.vh)
        }
    }) {
        // Header row
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                marginBottom(24.px)
                gap(16.px)
            }
        }) {
            H1({
                style {
                    fontSize(26.px)
                    fontWeight("bold")
                    color(Color("#0D2B2E"))
                    margin(0.px)
                    flex(1)
                }
            }) { Text("Order History") }

            Button({
                style {
                    backgroundColor(Color("#E0E7FF"))
                    color(Color("#1E1B4B"))
                    border(0.px)
                    padding(10.px, 16.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    fontSize(13.px)
                    fontWeight("600")
                }
                onClick { loadOrders() }
            }) { Text(if (isLoading) "Refreshing…" else "🔄 Refresh") }
        }

        when {
            isLoading -> {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        padding(60.px)
                        flexDirection(FlexDirection.Column)
                        gap(16.px)
                    }
                }) {
                    Div({ style { fontSize(48.px) } }) { Text("⏳") }
                    P({ style { color(Color("#757575")); fontSize(16.px); margin(0.px) } }) { Text("Loading your orders…") }
                }
            }
            errorMessage != null -> {
                Div({
                    style {
                        backgroundColor(Color("#FFEBEE"))
                        borderRadius(12.px)
                        padding(32.px)
                        textAlign("center")
                    }
                }) {
                    Div({ style { fontSize(48.px); marginBottom(12.px) } }) { Text("⚠️") }
                    P({ style { color(Color("#D32F2F")); fontSize(15.px); marginBottom(16.px) } }) { Text(errorMessage!!) }
                    Button({
                        style {
                            backgroundColor(Color("#D11243"))
                            color(Color.white)
                            border(0.px)
                            padding(10.px, 20.px)
                            borderRadius(8.px)
                            cursor("pointer")
                            fontSize(14.px)
                        }
                        onClick { loadOrders() }
                    }) { Text("Retry") }
                }
            }
            orders.isEmpty() -> EmptyOrdersState()
            else -> {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(16.px)
                    }
                }) {
                    orders.forEach { order -> ExpandableOrderCard(order) }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────
// Expandable Order Card
// ─────────────────────────────────────────────────────────
@Composable
private fun ExpandableOrderCard(order: WebOrder) {
    var expanded by remember { mutableStateOf(false) }

    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(14.px)
            property("box-shadow", "0 2px 10px rgba(0,0,0,0.09)")
            overflow("hidden")
            property("transition", "box-shadow 0.2s ease")
        }
        onMouseEnter { it.currentTarget.asDynamic().style.boxShadow = "0 4px 18px rgba(0,0,0,0.14)" }
        onMouseLeave { it.currentTarget.asDynamic().style.boxShadow = "0 2px 10px rgba(0,0,0,0.09)" }
    }) {
        // ── Summary Row (always visible, clickable) ──
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(18.px, 20.px)
                cursor("pointer")
                property("user-select", "none")
            }
            onClick { expanded = !expanded }
        }) {
            // Left: order id + date
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(4.px)
                    flex(1)
                }
            }) {
                P({
                    style {
                        fontSize(11.px)
                        color(Color("#9E9E9E"))
                        margin(0.px)
                        fontWeight("500")
                        property("letter-spacing", "0.05em")
                        property("text-transform", "uppercase")
                    }
                }) { Text("Order #${order.id.take(12).uppercase()}") }

                P({
                    style {
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color("#0D2B2E"))
                        margin(0.px)
                    }
                }) {
                    // First item name or count
                    val label = when {
                        order.items.isEmpty() -> "Order"
                        order.items.size == 1 -> order.items[0].name
                        else -> "${order.items[0].name} + ${order.items.size - 1} more"
                    }
                    Text(label)
                }

                P({
                    style {
                        fontSize(12.px)
                        color(Color("#757575"))
                        margin(0.px)
                    }
                }) { Text(formatOrderDate(order.createdAt)) }
            }

            // Centre: status badge
            Div({ style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center) } }) {
                OrderStatusBadge(order.status)
                if (order.paymentStatus.isNotBlank()) {
                    PaymentStatusBadge(order.paymentStatus)
                }
            }

            // Right: total + chevron
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.FlexEnd)
                    gap(4.px)
                    property("margin-left", "16px")
                }
            }) {
                P({
                    style {
                        fontSize(18.px)
                        fontWeight("bold")
                        color(Color("#D11243"))
                        margin(0.px)
                    }
                }) { Text("₹${order.totalAmount.toInt()}") }

                Span({
                    style {
                        fontSize(18.px)
                        color(Color("#9E9E9E"))
                        property("transition", "transform 0.2s ease")
                        property("display", "inline-block")
                        if (expanded) property("transform", "rotate(180deg)")
                    }
                }) { Text("▾") }
            }
        }

        // ── Expanded Detail Panel ──
        if (expanded) {
            Div({
                style {
                    property("border-top", "1px solid #F0F0F0")
                    padding(0.px, 20.px, 20.px, 20.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(20.px)
                }
            }) {
                // ── Items ──────────────────────────────────
                SectionHeading("🛒 Items Ordered")
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                    }
                }) {
                    order.items.forEach { item -> OrderItemDetailRow(item) }
                }

                // ── Price Breakup ───────────────────────────
                SectionHeading("💰 Price Breakup")
                Div({
                    style {
                        backgroundColor(Color("#FAFAFA"))
                        borderRadius(10.px)
                        padding(16.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(8.px)
                    }
                }) {
                    val itemsTotal = order.items.sumOf { it.price * it.quantity }
                    PriceRow("Items subtotal", "₹${itemsTotal.toInt()}", false)

                    if (order.totalAmount != itemsTotal && itemsTotal > 0) {
                        val diff = order.totalAmount - itemsTotal
                        if (diff > 0) PriceRow("Delivery / charges", "+ ₹${diff.toInt()}", false)
                        else if (diff < 0) PriceRow("Discount", "- ₹${(-diff).toInt()}", false)
                    }

                    Div({ style { property("border-top", "1px dashed #E0E0E0"); paddingTop(8.px) } }) {}
                    PriceRow("Total paid", "₹${order.payTodayAmount.toInt()}", true)
                }

                // ── Delivery Address ────────────────────────
                if (order.deliveryAddress.isNotBlank()) {
                    SectionHeading("📍 Delivery Address")
                    Div({
                        style {
                            backgroundColor(Color("#F4F9F9"))
                            borderRadius(10.px)
                            padding(14.px, 16.px)
                        }
                    }) {
                        P({
                            style {
                                fontSize(14.px)
                                color(Color("#212121"))
                                margin(0.px)
                                lineHeight("1.6")
                            }
                        }) { Text(order.deliveryAddress) }
                        if (order.customerName.isNotBlank()) {
                            P({
                                style {
                                    fontSize(13.px)
                                    color(Color("#757575"))
                                    margin(0.px)
                                    marginTop(4.px)
                                }
                            }) { Text("👤 ${order.customerName}  📞 ${order.customerPhone}") }
                        }
                    }
                }

                // ── Payment Info ────────────────────────────
                SectionHeading("💳 Payment")
                Div({
                    style {
                        backgroundColor(Color("#FAFAFA"))
                        borderRadius(10.px)
                        padding(14.px, 16.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }) {
                    InfoRow("Method", order.paymentMethod.ifBlank { "Cashfree" })
                    InfoRow("Payment status", order.paymentStatus.ifBlank { "—" })
                    if (order.cashfreeOrderId.isNotBlank()) {
                        InfoRow("Cashfree order ID", order.cashfreeOrderId)
                    }
                    if (order.paidAt > 0L) {
                        InfoRow("Paid on", formatOrderDate(order.paidAt))
                    }
                }

                // ── Status Timeline ─────────────────────────
                SectionHeading("📋 Order Status")
                Div({
                    style {
                        backgroundColor(Color("#FAFAFA"))
                        borderRadius(10.px)
                        padding(14.px, 16.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }) {
                    InfoRow("Current status", order.status.uppercase())
                    InfoRow("Placed on", formatOrderDate(order.createdAt))
                    if (order.updatedAt > 0L && order.updatedAt != order.createdAt) {
                        InfoRow("Last updated", formatOrderDate(order.updatedAt))
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────

@Composable
private fun SectionHeading(text: String) {
    P({
        style {
            fontSize(13.px)
            fontWeight("700")
            color(Color("#0D2B2E"))
            margin(0.px)
            marginTop(4.px)
            property("text-transform", "uppercase")
            property("letter-spacing", "0.06em")
        }
    }) { Text(text) }
}

@Composable
private fun PriceRow(label: String, value: String, bold: Boolean) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        P({
            style {
                fontSize(if (bold) 15.px else 14.px)
                fontWeight(if (bold) "700" else "400")
                color(if (bold) Color("#0D2B2E") else Color("#555"))
                margin(0.px)
            }
        }) { Text(label) }
        P({
            style {
                fontSize(if (bold) 16.px else 14.px)
                fontWeight(if (bold) "700" else "500")
                color(if (bold) Color("#D11243") else Color("#212121"))
                margin(0.px)
            }
        }) { Text(value) }
    }
}

@Composable
private fun InfoRow(label: String, value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        P({
            style {
                fontSize(13.px)
                color(Color("#9E9E9E"))
                margin(0.px)
                property("flex-shrink", "0")
            }
        }) { Text(label) }
        P({
            style {
                fontSize(13.px)
                color(Color("#212121"))
                margin(0.px)
                textAlign("right")
                fontWeight("500")
            }
        }) { Text(value) }
    }
}

@Composable
private fun OrderItemDetailRow(item: WebOrderItem) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(12.px)
            alignItems(AlignItems.Center)
            padding(10.px)
            backgroundColor(Color("#FAFAFA"))
            borderRadius(10.px)
        }
    }) {
        // Thumbnail
        Div({
            style {
                width(44.px)
                height(44.px)
                borderRadius(8.px)
                backgroundColor(Color("#EEEEEE"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                overflow("hidden")
                property("flex-shrink", "0")
            }
        }) {
            if (!item.imageUrl.isNullOrBlank()) {
                Img(src = item.imageUrl, attrs = {
                    style { width(100.percent); height(100.percent); property("object-fit", "cover") }
                })
            } else {
                Span({ style { fontSize(22.px) } }) { Text("🥗") }
            }
        }

        // Name + meta
        Div({
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
            }
        }) {
            P({
                style {
                    fontSize(14.px)
                    fontWeight("600")
                    color(Color("#212121"))
                    margin(0.px)
                }
            }) { Text(item.name) }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                val meta = buildList {
                    add("Qty: ${item.quantity}")
                    item.weight?.let { add(it) }
                    item.packSize?.let { add(it) }
                    item.scheduleSummary?.let { add(it) }
                    if (item.type == OrderItemType.SUBSCRIPTION) add("Subscription")
                }
                meta.forEach { tag ->
                    Span({
                        style {
                            fontSize(11.px)
                            color(Color("#757575"))
                            backgroundColor(Color("#F0F0F0"))
                            borderRadius(4.px)
                            padding(2.px, 6.px)
                        }
                    }) { Text(tag) }
                }
            }
        }

        // Price
        P({
            style {
                fontSize(14.px)
                fontWeight("700")
                color(Color("#0D2B2E"))
                margin(0.px)
                property("white-space", "nowrap")
            }
        }) { Text("₹${(item.price * item.quantity).toInt()}") }
    }
}

@Composable
private fun OrderStatusBadge(status: String) {
    val (bg, fg) = when (status.lowercase()) {
        "pending", "created"    -> "#FFF3E0" to "#E65100"
        "confirmed", "paid"     -> "#E3F2FD" to "#1565C0"
        "processing"            -> "#EDE7F6" to "#4527A0"
        "shipped", "dispatched" -> "#E8EAF6" to "#283593"
        "delivered"             -> "#E8F5E9" to "#2E7D32"
        "cancelled"             -> "#FFEBEE" to "#C62828"
        "payment_failed"        -> "#FFEBEE" to "#C62828"
        else                    -> "#F5F5F5" to "#616161"
    }
    Span({
        style {
            padding(4.px, 10.px)
            borderRadius(12.px)
            fontSize(11.px)
            fontWeight("700")
            backgroundColor(Color(bg))
            color(Color(fg))
            property("white-space", "nowrap")
            property("text-transform", "uppercase")
        }
    }) { Text(status) }
}

@Composable
private fun PaymentStatusBadge(status: String) {
    val (bg, fg) = when (status.uppercase()) {
        "PAID"     -> "#E8F5E9" to "#2E7D32"
        "PENDING"  -> "#FFF9C4" to "#F57F17"
        "FAILED"   -> "#FFEBEE" to "#C62828"
        "ACTIVE"   -> "#E3F2FD" to "#1565C0"
        else       -> "#F5F5F5" to "#616161"
    }
    Span({
        style {
            padding(3.px, 8.px)
            borderRadius(10.px)
            fontSize(10.px)
            fontWeight("700")
            backgroundColor(Color(bg))
            color(Color(fg))
            property("white-space", "nowrap")
            property("text-transform", "uppercase")
            border(1.px, LineStyle.Solid, Color(fg))
        }
    }) { Text(status) }
}

@Composable
private fun EmptyOrdersState() {
    Div({
        style {
            backgroundColor(Color.white)
            padding(60.px, 40.px)
            borderRadius(16.px)
            textAlign("center")
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
        }
    }) {
        Div({ style { fontSize(64.px); marginBottom(16.px) } }) { Text("📦") }
        H3({
            style {
                fontSize(22.px)
                fontWeight("bold")
                color(Color("#0D2B2E"))
                marginBottom(8.px)
            }
        }) { Text("No orders yet") }
        P({
            style {
                fontSize(15.px)
                color(Color("#757575"))
                lineHeight("1.6")
            }
        }) { Text("Your order history will appear here once you make your first purchase.") }
    }
}

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

private fun formatOrderDate(timestamp: Long): String {
    if (timestamp <= 0L) return "—"
    val date = Date(timestamp.toDouble())
    val months = arrayOf("Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec")
    val month = months[date.getMonth()]
    val day   = date.getDate().toString().padStart(2, '0')
    val year  = date.getFullYear()
    val h     = date.getHours()
    val m     = date.getMinutes().toString().padStart(2, '0')
    val ampm  = if (h >= 12) "PM" else "AM"
    val dh    = if (h > 12) h - 12 else if (h == 0) 12 else h
    return "$day $month $year • $dh:$m $ampm"
}
